type SortDirection = 'asc' | 'desc'

type SortKey<Item> = string | ((item: Item) => string | number)

/**
 * Sorts an array by a given key or accessor function.
 *
 * The sort key can be either a string property name or a function that
 * derives a comparable value from each item. Sorting is ascending by default.
 *
 * @template Item The type of the elements in the array.
 * @param array The array to sort.
 * @param direction The sort direction, 'asc' or 'desc'. Defaults to 'asc'.
 * @param sortKey The property name or accessor function to sort by. Defaults to 'title'.
 * @returns The sorted array.
 */
function sortArrayBy<Item>(
  array: Item[],
  direction: SortDirection = 'asc',
  sortKey: SortKey<Item> = 'title'
): Item[] {
  const isStringSortKey = typeof sortKey === 'string'

  const getSortColumn = (item: Item) =>
    isStringSortKey
      ? (item as Record<string, any>)[sortKey]
      : (sortKey as (item: Item) => string | number)(item)

  const sorted = [...array].sort((a, b) => {
    const compareA = getSortColumn(a)
    const compareB = getSortColumn(b)

    if (compareA > compareB) return direction === 'asc' ? 1 : -1
    if (compareA < compareB) return direction === 'asc' ? -1 : 1

    return 0
  })

  return sorted
}

export default sortArrayBy
